/**
 * W10f — READ A LONG `in.(…)` IN BATCHES, AND SAY WHEN THE ANSWER IS SHORT.
 *
 * 🔴 THE DEFECT THIS EXISTS FOR, MEASURED 2026-09-19. `ClientResults` read set logs with ONE
 * `.in("workout_session_exercise_id", …)` over EVERY session-exercise id the client had. His
 * client has **600**; at 37 characters per uuid that is a **22,336-character URL**, against a
 * common nginx limit of 8,192. On the render stub it returned **36 of 400 rows with
 * `error: null`** — a coach's Metrics tab showing a slice of a client's history and reporting
 * nothing at all. **A silent short read is how a coach concludes a client stopped training.**
 *
 * ⚠️ THE INNER-EMBED FORM WAS TRIED FIRST AND IS NOT USED. Selecting through
 * `workout_session_exercises!inner(workout_sessions!inner(user_id))` with an `.eq` on the nested
 * column is the right PostgREST shape and needs no id list at all — **but on the render stub it
 * returns 0 rows with `error: null`**, which is the same silent-failure shape one layer over.
 * Shipping it would have swapped a silent SHORT read for a silent EMPTY one, and the stub cannot
 * tell me which the live server would do. Batching is proven there at 400 of 400.
 *
 * ⚠️ AND THE COUNT IS THE POINT, not the batching. Batching alone would have fixed today's
 * symptom and left the next one silent; asking PostgREST for an EXACT count per batch and
 * comparing it to the rows received is what makes a truncation SAYABLE — including the 1,000-row
 * default cap, which no id list can avoid.
 */

/** What a batched read returned, and whether it is the whole answer. */
export type BatchedRead<T> = {
  rows: T[];
  /** What the server said exists, summed across batches. Null when no batch reported a count. */
  expected: number | null;
  /** True when every batch returned as many rows as it said existed. */
  complete: boolean;
  /** Batches that errored, by index — an empty list is not the same as a complete read. */
  failedBatches: number[];
};

/** Fifties: proven against the stub at 400 of 400, and ~1.9 KB of URL — an order under any limit. */
export const BATCH_SIZE = 50;

export function chunk<T>(items: readonly T[], size = BATCH_SIZE): T[][] {
  if (size < 1) throw new Error("chunk size must be at least 1");
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

/**
 * The sentence a screen shows when the read was short. Null when it was complete.
 *
 * ⚠️ IT NAMES BOTH NUMBERS. "Some sets are missing" is a sentence a coach cannot act on; "showing
 * 221 of 400 logged sets" tells them how much of the picture they have, which is the difference
 * between a caveat and a warning.
 */
export function shortReadNotice(read: BatchedRead<unknown>): string | null {
  if (read.failedBatches.length > 0) {
    return `Part of this client's history could not be read (${read.failedBatches.length} of `
      + `${read.failedBatches.length} batches failed). What is shown below is incomplete.`;
  }
  if (read.complete || read.expected === null) return null;
  return `Showing ${read.rows.length} of ${read.expected} logged sets — the rest could not be `
    + `loaded. What is shown below is incomplete.`;
}

/**
 * Merge per-batch results into one answer.
 *
 * ⚠️ AN UNREPORTED COUNT IS `null`, NEVER 0. A batch whose count the server did not give is not a
 * batch that found nothing — treating it as zero would make a complete read look short, and a
 * warning that cries wolf is one a coach learns to dismiss.
 */
export function mergeBatches<T>(
  results: readonly { rows: T[]; count: number | null; errored: boolean }[],
): BatchedRead<T> {
  const rows: T[] = [];
  let expected: number | null = null;
  const failedBatches: number[] = [];
  results.forEach((r, i) => {
    if (r.errored) { failedBatches.push(i); return; }
    rows.push(...r.rows);
    if (r.count !== null) expected = (expected ?? 0) + r.count;
  });
  const complete = failedBatches.length === 0 && (expected === null || rows.length >= expected);
  return { rows, expected, complete, failedBatches };
}
